/**
 * Enterprise Lesson 4: Video Switcher & Data Controllers
 * L1 format — breadcrumb instructions + voiceover.
 * Shipped SILENT 2026-08-25: narration not yet recorded, so every step's audioUrl is
 * left commented out. Uncomment the audioUrl lines when Lesson_E4-01…11.mp3 land.
 */
import type { EnterpriseLessonDef } from '@graphicsim/shared-types';

// TODO: audio narration files (Lesson_E4-*.mp3) to be recorded.
// Leave audioUrl undefined for now — Next button enables immediately without audio.
export const entGfx04: EnterpriseLessonDef = {
  id: 'ent-gfx-04',
  name: 'Video Switcher & Data Controllers',
  mode: 'steps',
  sections: [
    // Section 1: Switcher Panel
    {
      id: 'sec-1',
      title: 'Video Switcher Panel',
      steps: [
        {
          id: '1a',
          type: 'info',
          title: 'About the Video Switcher',
          instruction: 'NEVCO Pro Video includes a Video Switcher panel for cutting between live camera inputs and graphics. The panel itself is in active development — for this lesson you will just confirm it opens, then move on to connecting a live data feed from ScoreBird.',
          // audioUrl: 'TODO: Lesson_E4-01.mp3',
        },
        {
          id: '1b',
          type: 'action',
          title: 'Open the Switcher Panel',
          instruction: 'Click the Switcher button in the toolbar to show the Video Switcher panel.',
          highlightSelector: '#icon-toolbar',
          completionTrigger: {
            eventName: 'ui:panelToggled',
            matchField: 'panel',
            matchValue: 'switcher',
          },
          allowedSelectors: ['#icon-toolbar'],
          // audioUrl: 'TODO: Lesson_E4-02.mp3',
        },
      ],
    },
    // Section 2: Data Controller Menu
    {
      id: 'sec-2',
      title: 'Data Controllers',
      steps: [
        {
          id: '2a',
          type: 'info',
          title: 'What is a Data Controller?',
          instruction: 'A Data Controller connects a graphics template to a live data source, so fields like score, clock, and team name update automatically on the Program Monitor.',
          // audioUrl: 'TODO: Lesson_E4-03.mp3',
        },
        {
          id: '2b',
          type: 'action',
          title: 'Add a Football Template',
          instruction: 'Open the Football folder in the Graphics Library and drag "Football Bottom Scoreboard" into the Project panel.',
          highlightSelector: '#panel-library',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Bottom Scoreboard',
          },
          allowedSelectors: ['#panel-library', '#panel-project'],
          // audioUrl: 'TODO: Lesson_E4-04.mp3',
        },
        {
          id: '2c',
          type: 'action',
          title: 'Select the Scoreboard',
          instruction: 'Click the scoreboard row in the Project panel so its Properties appear on the right.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'selection:changed',
            matchField: 'target.type',
            matchValue: 'projectItem',
          },
          allowedSelectors: ['#panel-project'],
          // audioUrl: 'TODO: Lesson_E4-05.mp3',
        },
      ],
    },
    // Section 3: ScoreBird NeST Connection
    {
      id: 'sec-3',
      title: 'Connect ScoreBird',
      steps: [
        {
          id: '3a',
          type: 'info',
          title: 'ScoreBird NeST',
          instruction: 'ScoreBird is a live scoreboard-data service. You will now pick a ScoreBird source from the Data Controller menu in the Properties panel.',
          // audioUrl: 'TODO: Lesson_E4-06.mp3',
        },
        {
          id: '3b',
          type: 'action',
          title: 'Open Data Controller Menu',
          instruction: 'In the Properties panel, click the Data Controller dropdown and choose Sports > Scorebird > Football > New Input.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'data:newInputRequested',
            matchField: 'sport',
            matchValue: 'football',
          },
          allowedSelectors: ['#panel-properties', '.dc-menu', '.dc-submenu'],
          // audioUrl: 'TODO: Lesson_E4-07.mp3',
        },
        {
          id: '3c',
          type: 'action',
          title: 'Enter a NeST ID and Connect',
          instruction: 'In the ScoreBird Settings tab, enter your NeST ID and click Connect to API Server. The Live Data view appears once the connection is established. If your ScoreBird has not arrived yet, leave the demo unit that is already filled in and connect to that instead.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'data:sourceCreated',
          },
          allowedSelectors: ['#panel-properties'],
          // audioUrl: 'TODO: Lesson_E4-08.mp3',
        },
      ],
    },
    // Section 4: Live Data & Auto-linking
    {
      id: 'sec-4',
      title: 'Live Data',
      steps: [
        {
          id: '4a',
          type: 'info',
          title: 'Fields Auto-Linked',
          instruction: 'When a ScoreBird source connects, matching fields on your template are auto-linked to the live data. You should see yellow highlight flashes on updated values.',
          highlightSelector: '#panel-properties',
          // audioUrl: 'TODO: Lesson_E4-09.mp3',
        },
        {
          id: '4b',
          type: 'action',
          title: 'Take Scoreboard to Program',
          instruction: 'Click the Play button on the scoreboard row to send it live. Live data will now drive the fields on the Program Monitor.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
            matchField: 'itemName',
            matchValue: 'contains:Bottom Scoreboard',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
          // audioUrl: 'TODO: Lesson_E4-10.mp3',
        },
        {
          id: '4c',
          type: 'info',
          title: 'Lesson Complete',
          instruction: 'You connected a scoreboard template to a live ScoreBird data feed and sent it to the Program Monitor. Your scoreboard now updates automatically as the game progresses.',
          // audioUrl: 'TODO: Lesson_E4-11.mp3',
        },
      ],
    },
  ],
};
